import type { z } from "zod";

import { NEEDS_ANALYSIS_FIELDS } from "./schemas";
import type {
  onboardingStepSchema,
  roleSchema,
  ssoProviderSchema,
  tenantStatusSchema,
  trainingSessionSchema,
  userStatusSchema,
} from "./responses";
import type {
  chatModeSchema,
  difficultySchema,
  feedbackDimensionSchema,
  GespraechsPhase,
  NeedsAnalysisFieldKey,
  PlaybookBaustein,
  realtimeVoiceSchema,
  TrainingsEinheit,
} from "./schemas";

/**
 * Domänenbegriffe, die auf beiden Seiten gebraucht werden.
 *
 * Die Antwortformen selbst stehen in `responses.ts` und werden aus den
 * Zod-Schemas abgeleitet, damit Typ und OpenAPI-Spec nicht auseinanderlaufen
 * können. Hier bleibt nur, was Anzeige- und Fachlogik ist und nicht über die
 * Leitung geht.
 */

export type Role = z.infer<typeof roleSchema>;
/** Rollen, die innerhalb einer Sparkasse vergeben werden können. */
export type TenantRole = Exclude<Role, "platform">;
export type UserStatus = z.infer<typeof userStatusSchema>;
export type TenantStatus = z.infer<typeof tenantStatusSchema>;
export type OnboardingStep = z.infer<typeof onboardingStepSchema>;
export type SsoProvider = z.infer<typeof ssoProviderSchema>;
export type Difficulty = z.infer<typeof difficultySchema>;
export type ChatMode = z.infer<typeof chatModeSchema>;
export type RealtimeVoice = z.infer<typeof realtimeVoiceSchema>;

export type SessionMode = z.infer<typeof trainingSessionSchema>["mode"];
export type FeedbackDimension = z.infer<typeof feedbackDimensionSchema>;

export const REALTIME_VOICES: RealtimeVoice[] = [
  "alloy",
  "ash",
  "ballad",
  "coral",
  "echo",
  "sage",
  "shimmer",
  "verse",
  "marin",
  "cedar",
];

export const DIFFICULTY_LABELS: Record<Difficulty, string> = {
  entry: "Einstieg",
  medium: "Mittel",
  advanced: "Fordernd",
};

/** Ascending; drives the group order in the case picker. */
export const DIFFICULTY_ORDER: Difficulty[] = ["entry", "medium", "advanced"];

export const ROLE_LABELS: Record<Role, string> = {
  advisor: "Berater",
  coach: "Coach",
  manager: "Führungskraft",
  platform: "Admin",
  content_admin: "Inhaltspflege",
};

export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  active: "aktiv",
  invited: "eingeladen",
  disabled: "deaktiviert",
};

export const ONBOARDING_STEP_LABELS: Record<OnboardingStep, string> = {
  selected: "Ausgewählt",
  setup: "Setup",
  onboarding: "Onboarding",
  live: "Live",
};

export const TENANT_STATUS_LABELS: Record<TenantStatus, string> = {
  active: "aktiv",
  paused: "pausiert",
};

export const SSO_PROVIDER_LABELS: Record<SsoProvider, string> = {
  oidc: "OIDC",
  saml: "SAML 2.0",
  azuread: "Azure AD",
};

export const GESPRAECHSTRAINING_LABEL = "Gesprächstraining";

/**
 * Stands in for the coach's name until it is loaded. There is deliberately no
 * constant for the coach chat itself: it carries the name of the coach the
 * advisor picked, and a fixed label would reintroduce the second figure this
 * wording was meant to remove.
 */
export const COACH_FALLBACK_LABEL = "KI-Coach";

/**
 * What the advisor trains is a customer conversation. "Persona" is a term from
 * building the application: it stays in the code and leaves the interface.
 */
export const KI_KUNDE_LABEL = "KI-Kunde";

export const CHAT_MODE_LABELS: Record<ChatMode, string> = {
  open: "Freier Austausch",
  // Nicht "Produkttraining": der Modus prüft die Gesprächsführung, und der
  // Prompt verbietet jede Produktaussage ausdrücklich.
  training: "Abfrage",
  onboarding: "Kennenlernen",
  preparation: "Terminvorbereitung",
  nachbereitung: "Nachbereitung",
  pruefung: "Prüfungsvorbereitung",
};

export const IVFP_PHASE_LABELS: Record<GespraechsPhase, string> = {
  terminierung: "Terminierung",
  gespraechsvorbereitung: "Gesprächsvorbereitung",
  gespraechsrahmen: "Gesprächsrahmen",
  bedarfsanalyse: "Bedarfsanalyse",
  loesung_abschluss: "Lösungspräsentation & Abschluss",
};

/** Kanonische Reihenfolge. Ein Durchlaufplan ist eine Teilmenge in dieser Folge. */
export const IVFP_PHASE_ORDER: GespraechsPhase[] = [
  "terminierung",
  "gespraechsvorbereitung",
  "gespraechsrahmen",
  "bedarfsanalyse",
  "loesung_abschluss",
];

/**
 * Die abwählbaren Bausteine des Leitfadens.
 *
 * Eine Tabelle und keine verstreuten Bedingungen, aus demselben Grund wie bei
 * `TRAINING_EINHEITEN`: was ein Baustein ist, wie er heißt und ob er
 * vorausgewählt ist, ist Daten. Die Texte gehören mit hierher, weil der
 * Konfigurator sie braucht und zwei Listen auseinanderlaufen würden.
 *
 * Es gibt bewusst keine Funktion „ist Baustein X aktiv". Aktiv ist ein Baustein
 * immer nur in einem konkreten Durchlauf (`ProcessRun.bausteine`); eine globale
 * Antwort darauf wäre eine zweite und falsche Wahrheit.
 */
export const PLAYBOOK_BAUSTEINE: {
  key: PlaybookBaustein;
  /** Beschriftung des Schalters und Marke im Durchlauf. */
  label: string;
  /** Eine Zeile, die die Wahl begründet. */
  beschreibung: string;
  /** Im Konfigurator vorausgewählt. */
  standard: boolean;
}[] = [
  {
    key: "einkommensschutz",
    label: "Einkommensschutz",
    beschreibung: "Der Anker zum Einkommensschutz in Bedarfsanalyse und Lösung.",
    standard: true,
  },
];

/**
 * Alle wählbaren Bausteine.
 *
 * Auch der Rückfall, wo es keinen Durchlauf gibt — im offenen Chat und im
 * Abfrage-Modus gehört der ganze Lehrgang zum Stoff.
 */
export const VERFUEGBARE_BAUSTEINE: PlaybookBaustein[] = PLAYBOOK_BAUSTEINE.map(
  (b) => b.key,
);

/** Die Vorauswahl im Konfigurator, und die Voreinstellung der API. */
export const STANDARD_BAUSTEINE: PlaybookBaustein[] = PLAYBOOK_BAUSTEINE.filter(
  (b) => b.standard,
).map((b) => b.key);

export function bausteinDefinition(baustein: PlaybookBaustein) {
  const treffer = PLAYBOOK_BAUSTEINE.find((b) => b.key === baustein);
  if (!treffer) throw new Error(`Unbekannter Baustein: ${baustein}`);
  return treffer;
}

/**
 * The leading questions of the follow-up, and with them the fields of the
 * Nachbereitungsbogen.
 *
 * One table instead of two lists: the question stands next to the key its
 * answer is stored under, so an answer in `caseState` finds its way back to
 * its question without a second mapping that would drift. The unit's
 * `leitfragen` below are derived from here.
 */
export const NACHBEREITUNG_FRAGEN: {
  key: string;
  /** Field label on the sheet; the full question stands in the list above it. */
  kurz: string;
  frage: string;
}[] = [
  {
    key: "ergebnis",
    kurz: "Ergebnis des Gesprächs",
    frage:
      "Zu welchem Ergebnis ist das Beratungsgespräch gekommen, und was konnte mit dem Kunden abschließend geklärt werden?",
  },
  {
    key: "ausstehend",
    kurz: "Steht kundenseitig noch aus",
    frage:
      "Welche Unterlagen oder Informationen stehen kundenseitig noch aus (zum Beispiel Bedenkzeit, fehlende Unterlage, der Kunde prüft)?",
  },
  {
    key: "offene_fragen",
    kurz: "Nachzureichen",
    frage:
      "Welche Fragen des Kunden sind noch offen und müssen nachgereicht werden?",
  },
  {
    key: "naechster_kontakt",
    kurz: "Nächster Kontakt",
    frage: "Wann ist der nächste Kontakt mit dem Kunden vorgesehen?",
  },
  {
    key: "naechstes_thema",
    kurz: "Anlass beim nächsten Kontakt",
    frage: "Welches Thema oder welcher Anlass steht beim nächsten Kontakt an?",
  },
];

/**
 * The training units an advisor assembles a run from.
 *
 * The unit is the training axis, the phase stays below it as the content key.
 * Two units bundle more than one phase because they are walked together in a
 * real conversation anyway, and therefore share one feedback.
 *
 * Everything that used to be a special case in code — which unit is a roleplay,
 * which produces a feedback, which happens on the phone — is a property here.
 * Whoever touches a unit changes this table, not five scattered `if` branches.
 */
export const TRAINING_EINHEITEN: {
  key: TrainingsEinheit;
  label: string;
  /** For the stepper and the progress rail, where the full label is truncated. */
  shortLabel: string;
  /** One line for the stage header: what the advisor does in this unit. */
  description: string;
  /** The phases this unit trains, in canonical order. */
  phasen: GespraechsPhase[];
  /**
   * `rollenspiel` opens a session against the AI customer, `sparring` one
   * against the coach. Drives session creation and the stage that is rendered,
   * instead of a hardcoded unit name.
   */
  interaktion: "rollenspiel" | "sparring";
  /**
   * Whether the unit shows the debrief over the whole run.
   *
   * Its own property and no longer a third `interaktion`: since the follow-up
   * got its leading questions it is BOTH — a sparring about the follow-up, and
   * after that the report. One field cannot carry both answers.
   */
  zeigtGesamtbild: boolean;
  /** Whether the unit ends in a reflection conversation and a feedback. */
  hatFeedback: boolean;
  /**
   * How much self-assessment the coach asks for before opening the analysis.
   * `kurz` fits a unit that only takes a few minutes, such as the phone call.
   */
  reflexionsTiefe: "kurz" | "voll";
  /**
   * `nurEinzeln`: inside a longer run the feedback still exists — it carries the
   * IDD record and feeds the debrief — but it does not get its own document.
   */
  dokument: "immer" | "nurEinzeln";
  /** On the phone the advisor must not see the customer. */
  kanal: "telefon" | "praesenz" | "keiner";
  /**
   * What the coach asks the unit along. Only sparring units carry any: where a
   * roleplay happens, there are no leading questions.
   *
   * Here and not only in the playbook because both sides need them — the prompt
   * in the backend and the stage in the frontend. Two lists would drift apart.
   *
   * The follow-up reaches the prompt from here directly and not through the
   * playbook corpus: that one is keyed by phase, and the follow-up has none.
   */
  leitfragen: string[];
  /** Card header, absolute path under the web app's public folder. */
  imageUrl: string;
  /** Units without coaching content stay visible but unselectable. */
  verfuegbar: boolean;
}[] = [
  {
    key: "terminierung",
    label: "Terminierung",
    shortLabel: "Terminierung",
    description:
      "Den Kunden anrufen, über den Anlass ins Gespräch kommen, Einwände auffangen und den Termin konkret machen.",
    phasen: ["terminierung"],
    interaktion: "rollenspiel",
    zeigtGesamtbild: false,
    hatFeedback: true,
    reflexionsTiefe: "kurz",
    dokument: "nurEinzeln",
    kanal: "telefon",
    leitfragen: [],
    imageUrl: "/phasen/terminierung.jpg",
    verfuegbar: true,
  },
  {
    key: "gespraechsvorbereitung",
    label: "Gesprächsvorbereitung",
    shortLabel: "Vorbereitung",
    description:
      "Entlang der Leitfragen mit dem Coach durchgehen, was du über den Kunden weißt und was du daraus schließt.",
    phasen: ["gespraechsvorbereitung"],
    // Sparring instead of roleplay: the coach asks, there is no customer.
    interaktion: "sparring",
    zeigtGesamtbild: false,
    // Explicitly no feedback, straight over into the advisory conversation.
    hatFeedback: false,
    reflexionsTiefe: "kurz",
    dokument: "immer",
    kanal: "keiner",
    // The three points of the master handout, phrased as questions. What is
    // trained is not reciting the master data but the conclusion drawn from it —
    // hence every question asks for the "and what does that mean".
    leitfragen: [
      "Vertriebschancen und Anlässe: Was ist bei diesem Kunden gerade der Anlass, und woran machst du das fest?",
      "Kundeninformationen: Was weißt du über Einkommen, Familie, Wohnsituation und Bestand, und wo ist die Angabe alt oder unsicher?",
      "AV-Tool und WRZ: Welche Zahlen brauchst du im Termin auf dem Schirm, und welche willst du gemeinsam mit dem Kunden erarbeiten?",
      "Was ist dein konkretes Ziel für diesen Termin, und woran merkst du hinterher, dass du es erreicht hast?",
    ],
    imageUrl: "/phasen/gespraechsvorbereitung.jpg",
    verfuegbar: true,
  },
  {
    key: "rahmen_bedarf",
    label: "Gesprächsrahmen & Bedarfsanalyse",
    shortLabel: "Rahmen & Bedarf",
    description:
      "Zeitrahmen, Erwartungen und Ziel klären und daraus ohne Bruch in Wunschrente, Zielrente und Vorsorgelücke gehen.",
    // Both phases run in one conversation. The success criterion is the agreed
    // follow-up appointment, not the sale.
    phasen: ["gespraechsrahmen", "bedarfsanalyse"],
    interaktion: "rollenspiel",
    zeigtGesamtbild: false,
    hatFeedback: true,
    reflexionsTiefe: "voll",
    dokument: "immer",
    kanal: "praesenz",
    leitfragen: [],
    imageUrl: "/phasen/bedarfsanalyse.jpg",
    verfuegbar: true,
  },
  {
    key: "loesung_abschluss",
    label: "Lösungspräsentation & Abschluss",
    shortLabel: "Lösung & Abschluss",
    description:
      "Schockrate und Beitragsobergrenze einordnen, die Lösung vorstellen und Einwände im Abschluss auffangen.",
    phasen: ["loesung_abschluss"],
    interaktion: "rollenspiel",
    zeigtGesamtbild: false,
    hatFeedback: true,
    reflexionsTiefe: "voll",
    dokument: "immer",
    kanal: "praesenz",
    leitfragen: [],
    imageUrl: "/phasen/loesung_abschluss.jpg",
    verfuegbar: true,
  },
  {
    key: "nachbereitung",
    label: "Nachbereitung",
    shortLabel: "Nachbereitung",
    description:
      "Das Gesamtbild über die trainierten Einheiten lesen und mit dem Coach durchgehen.",
    // No phase of its own: the follow-up is not one of the five IVFP phases,
    // and `einheitFuerPhase` has to stay the inverse of `phasenDerEinheit`.
    // It does get a session all the same — see `zeigtGesamtbild`.
    phasen: [],
    // Sparring like the preparation: the advisor answers the leading questions
    // with the coach. The report follows afterwards, hence `zeigtGesamtbild`.
    interaktion: "sparring",
    zeigtGesamtbild: true,
    hatFeedback: false,
    reflexionsTiefe: "kurz",
    dokument: "immer",
    kanal: "keiner",
    leitfragen: NACHBEREITUNG_FRAGEN.map((f) => f.frage),
    imageUrl: "/phasen/gespraechsrahmen.jpg",
    verfuegbar: true,
  },
];

export const EINHEIT_LABELS: Record<TrainingsEinheit, string> =
  Object.fromEntries(
    TRAINING_EINHEITEN.map((e) => [e.key, e.label]),
  ) as Record<TrainingsEinheit, string>;

/** Canonical order. A run plan is a subset in this sequence. */
export const EINHEIT_ORDER: TrainingsEinheit[] = TRAINING_EINHEITEN.map(
  (e) => e.key,
);

export function einheitDefinition(einheit: TrainingsEinheit) {
  const treffer = TRAINING_EINHEITEN.find((e) => e.key === einheit);
  if (!treffer) throw new Error(`Unbekannte Trainingseinheit: ${einheit}`);
  return treffer;
}

/** The phases a unit trains, in canonical order. */
export function phasenDerEinheit(einheit: TrainingsEinheit): GespraechsPhase[] {
  return einheitDefinition(einheit).phasen;
}

/**
 * The unit a phase belongs to. Undefined only for phases no unit claims, which
 * cannot happen today but keeps old rows readable if the cut ever changes.
 */
export function einheitFuerPhase(
  phase: GespraechsPhase,
): TrainingsEinheit | undefined {
  return TRAINING_EINHEITEN.find((e) => e.phasen.includes(phase))?.key;
}

/**
 * What the worksheet counts in this unit.
 *
 * On the phone there is exactly one point: the appointment. The seven figures
 * of the needs analysis belong in the branch appointment, and their sidecar
 * only runs there — during the appointment call the counter would otherwise
 * sit at 0/7 for good, because nothing can ever fill it.
 *
 * One function instead of conditions spread over header, progress bar and
 * sheet body: what is counted is data, and three copies would drift.
 */
export function arbeitsblattStand(
  einheit: TrainingsEinheit,
  /**
   * Structural, not `CaseState`: the web app holds the generated client's
   * shape, whose Zod defaults make it a different-but-equivalent type. Asking
   * only for what is counted keeps both callers out of a cast.
   */
  caseState: {
    appointment?: { termin?: unknown } | null;
    needsAnalysis?: Partial<Record<NeedsAnalysisFieldKey, unknown>> | null;
  },
): { erfasst: number; gesamt: number } {
  if (phasenDerEinheit(einheit).includes("terminierung")) {
    return { erfasst: caseState.appointment?.termin ? 1 : 0, gesamt: 1 };
  }
  return {
    erfasst: NEEDS_ANALYSIS_FIELDS.filter(
      ({ key }) => caseState.needsAnalysis?.[key],
    ).length,
    gesamt: NEEDS_ANALYSIS_FIELDS.length,
  };
}

/** Which set of fields the worksheet body shows. */
export function arbeitsblattFelder(
  einheit: TrainingsEinheit,
): "termin" | "bedarfsanalyse" {
  return phasenDerEinheit(einheit).includes("terminierung")
    ? "termin"
    : "bedarfsanalyse";
}

/** Dedupe and sort a requested selection into canonical order. */
export function orderEinheiten(
  requested: readonly TrainingsEinheit[],
): TrainingsEinheit[] {
  return EINHEIT_ORDER.filter((e) => requested.includes(e));
}

/** The units the advisor can train today, wherever they are entered from. */
export const TRAINABLE_EINHEITEN: TrainingsEinheit[] = TRAINING_EINHEITEN
  .filter((e) => e.verfuegbar)
  .map((e) => e.key);

/** The units a run plan can be assembled from. */
export const DURCHLAUF_EINHEITEN: TrainingsEinheit[] = TRAINING_EINHEITEN
  .filter((e) => e.verfuegbar)
  .map((e) => e.key);

/**
 * Derived from the units on purpose: unlocking a unit is a data change in
 * `TRAINING_EINHEITEN` and must not need a second edit here.
 *
 * Shared rather than local to the configurator: the product training needs the
 * same answer when it picks the phase its questions come from.
 */
export const TRAINABLE_PHASES: GespraechsPhase[] = IVFP_PHASE_ORDER.filter(
  (phase) =>
    TRAINING_EINHEITEN.some((e) => e.verfuegbar && e.phasen.includes(phase)),
);

/**
 * Whether this unit closes the run with the debrief over everything before it.
 *
 * It trains no phase of its own, so a plan that holds nothing else has nothing
 * to show. What it does have since the follow-up questions: a sparring session
 * with the coach — the report is its second step, not its only content.
 */
export function istGesamtbild(einheit: TrainingsEinheit): boolean {
  return einheitDefinition(einheit).zeigtGesamtbild;
}

/** The units of a plan that actually train something. */
export function trainingsEinheiten(
  units: readonly TrainingsEinheit[],
): TrainingsEinheit[] {
  return units.filter((u) => !istGesamtbild(u));
}

/**
 * Whether this unit opens a feedback document of its own in this run.
 *
 * `nurEinzeln` units get one only when they are the whole run: after a
 * two-minute phone call inside a longer session a document is more apparatus
 * than insight, and its content goes into the debrief instead (NL-20).
 *
 * Both sides read this: the interface decides whether to open the column, and
 * the feedback prompt decides whether the coach may point at it. They must not
 * disagree — a coach who says "it's on the right" while nothing is there is
 * worse than no closing line at all.
 */
export function zeigtEigenesDokument(
  einheit: TrainingsEinheit,
  einheitenImDurchlauf: number,
): boolean {
  return (
    einheitDefinition(einheit).dokument === "immer" || einheitenImDurchlauf <= 1
  );
}

/** What is settled once this phase has happened, told to the advisor. */
export const PHASE_VORGESCHICHTE: Record<GespraechsPhase, string> = {
  terminierung:
    "Du hast angerufen und einen Termin in der Filiale vereinbart.",
  gespraechsvorbereitung:
    "Du bist vorbereitet: Stammdaten gesichtet, Anlass geprüft, AV-Tool offen.",
  gespraechsrahmen:
    "Ihr sitzt nun zusammen. Der konkrete Inhalt und das Ziel des Termins werden zu Beginn gemeinsam geklärt.",
  bedarfsanalyse:
    "Die Haushaltsrechnung steht, die Lücke habt ihr gemeinsam erarbeitet.",
  loesung_abschluss:
    "Die Lösung liegt auf dem Tisch, über den Abschluss habt ihr gesprochen.",
};

/**
 * Was ein Baustein zur Vorgeschichte einer übersprungenen Phase hinzufügt.
 *
 * Ohne den Zusatz beginnt ein Durchlauf, der erst bei der Lösung einsteigt, ohne
 * den Anker, auf den die Budgetfrage sich beruft — die Beraterin soll nicht nach
 * etwas fragen, wovon sie nicht weiß, dass es passiert ist. Die Vorbereitung
 * bleibt bewusst außen vor: dort ist die Vorab-Berechnung eine Pflichtzeile im
 * Bogen und damit sichtbar, nicht Erzählung.
 */
const BAUSTEIN_VORGESCHICHTE: Partial<
  Record<PlaybookBaustein, Partial<Record<GespraechsPhase, string>>>
> = {
  einkommensschutz: {
    bedarfsanalyse:
      "Den Einkommensschutz hast du angesprochen; der Kunde ist einverstanden, das Thema später zu vertiefen.",
  },
};

/**
 * The backstory of a phase: only the preceding phases the run **skips**.
 * Whoever played the appointment call themselves does not get told how it went.
 */
export function phasenVorgeschichte(
  phase: GespraechsPhase,
  phasenImDurchlauf: readonly GespraechsPhase[],
  /** Die Bausteine dieses Durchlaufs. Leer heißt abgewählt, nicht ungesetzt. */
  bausteine: readonly PlaybookBaustein[] = [],
): string[] {
  const index = IVFP_PHASE_ORDER.indexOf(phase);
  if (index < 1) return [];
  return IVFP_PHASE_ORDER.slice(0, index)
    .filter((vorher) => !phasenImDurchlauf.includes(vorher))
    .map((vorher) =>
      [
        PHASE_VORGESCHICHTE[vorher],
        ...bausteine
          .map((b) => BAUSTEIN_VORGESCHICHTE[b]?.[vorher])
          .filter((satz): satz is string => !!satz),
      ].join(" "),
    );
}

/**
 * The backstory of a unit: what happened before its first phase and was not
 * played in this run.
 *
 * Expands the chosen units into phases first, so a unit that carries two phases
 * does not tell the advisor about its own second half.
 */
export function einheitenVorgeschichte(
  einheit: TrainingsEinheit,
  einheitenImDurchlauf: readonly TrainingsEinheit[],
  bausteine: readonly PlaybookBaustein[] = [],
): string[] {
  const [erste] = phasenDerEinheit(einheit);
  if (!erste) return [];
  const gespielt = einheitenImDurchlauf.flatMap(phasenDerEinheit);
  return phasenVorgeschichte(erste, gespielt, bausteine);
}

/**
 * The unit this one continues as the same conversation, if any.
 *
 * Only a face-to-face roleplay directly after another one in the same run: the
 * appointment call and the branch appointment are two conversations on two
 * channels, and a sparring in between breaks the scene. Decided by channel
 * and interaction rather than by name, so today this is exactly
 * "Rahmen & Bedarf" → "Lösung & Abschluss".
 */
export function fortsetzungVon(
  einheit: TrainingsEinheit,
  einheitenImDurchlauf: readonly TrainingsEinheit[],
): TrainingsEinheit | undefined {
  const plan = orderEinheiten(einheitenImDurchlauf);
  const index = plan.indexOf(einheit);
  if (index < 1) return undefined;
  const davor = plan[index - 1];
  const imGespraech = (e: TrainingsEinheit) => {
    const d = einheitDefinition(e);
    return d.interaktion === "rollenspiel" && d.kanal === "praesenz";
  };
  return imGespraech(einheit) && imGespraech(davor) ? davor : undefined;
}

/**
 * Der fachliche Rahmen des Lehrgangs. Ersetzt den früheren Produktkontext.
 *
 * Der IVFP-Vorsorgeberater zielt auf das ratierliche Vorsorgegeschäft
 * (Masterunterlage S. 1 und S. 27). Der Einstieg läuft nicht über ein Produkt,
 * sondern über das „Einkommensgutachten" als kostenfreien Service (Handout 1).
 * Ein Produkt kommt erst in der Lösungspräsentation vor.
 */
export const IVFP_LEHRGANG = {
  name: "IVFP-Vorsorgeberater",
  geschaeftsfeld: "Ratierliches Vorsorgegeschäft, private Altersvorsorge",
  anlass: "Einkommensgutachten als kostenfreier Service der Sparkasse",
  zielsortiment:
    "neue leben Aktivplan (klassisch, hybrid, fondsgebunden), laufender Beitrag",
  knowledgeVersion: "IVFP-Vorsorgeberater 03.2026",
} as const;

export type TrainingsAnlassKey =
  | "einkommensgutachten"
  | "einkommensabsicherung"
  | "baufinanzierung";

/**
 * The occasions an advisor can train on.
 *
 * An occasion is not a process: the five phases in `IVFP_PHASE_ORDER` apply to
 * all of them alike, what differs are the cases, the playbook and the technical
 * side of the feedback. It is therefore a display list and not an axis in the
 * data model. Once a second occasion carries real content it belongs on the
 * case, not on the advisor's configuration.
 *
 * Only `einkommensgutachten` is backed by content. The other two are shown on
 * purpose, so the PoC reads as a platform rather than as a single feature, and
 * they must stay unselectable as long as there is neither a case nor a playbook
 * behind them.
 */
export const TRAINING_ANLAESSE: {
  key: TrainingsAnlassKey;
  label: string;
  /** For the stepper, where the full label would be truncated mid-word. */
  shortLabel: string;
  description: string;
  /** Card header, relative path under /themen/. Decorative: the title carries the meaning. */
  imageUrl: string;
  available: boolean;
}[] = [
  {
    key: "einkommensgutachten",
    label: "Einkommensgutachten trainieren",
    shortLabel: "Einkommensgutachten",
    // Deliberately says nothing about how much of the conversation is covered:
    // round one trains the need analysis only, and the phase picker below the
    // card is the honest place for that.
    description:
      "Das Gespräch über die finanzielle Zukunft deines Kunden, das mit dem kostenfreien Einkommensgutachten beginnt.",
    imageUrl: "/themen/einkommensgutachten.jpg",
    available: true,
  },
  {
    key: "einkommensabsicherung",
    label: "Einkommensabsicherung trainieren",
    shortLabel: "Einkommensabsicherung",
    description:
      "Das Gespräch über die Absicherung der Arbeitskraft: was bleibt, wenn das Einkommen durch Krankheit oder Berufsunfähigkeit wegfällt.",
    imageUrl: "/themen/einkommensabsicherung.jpg",
    available: false,
  },
  {
    key: "baufinanzierung",
    label: "Absicherung der Baufinanzierung trainieren",
    shortLabel: "Baufinanzierung",
    description:
      "Das Gespräch rund um die Immobilienfinanzierung: wie Kunden ihr Darlehen absichern, wenn ein Einkommen ausfällt.",
    imageUrl: "/themen/baufinanzierung.jpg",
    available: false,
  },
];

export type ProduktKey =
  | "aktivplan"
  | "basisrente"
  | "berufsunfaehigkeit"
  | "foerderrente";

/**
 * The products the advisor can be quizzed on.
 *
 * Same shape and the same rule as `TRAINING_ANLAESSE`: what is not backed by
 * material stays visible but unselectable. The bar is higher here, because a
 * product conversation without the terms and conditions would invent numbers.
 * `prompts.ts` therefore forbids product statements outright until the material
 * is in, see PRODUCT_KNOWLEDGE_RULE.
 */
export const TRAINING_PRODUKTE: {
  key: ProduktKey;
  label: string;
  description: string;
  /** Card header, relative path under /produkte/. Decorative: the title carries the meaning. */
  imageUrl: string;
  /**
   * Opening chips in the empty chat. They are sent verbatim as the advisor's
   * first message, so they have to read as a request and has to be covered by
   * the seeded snippets: a chip pointing at material that is not there produces
   * exactly the "steht nicht in meinen Unterlagen" answer it promised to avoid.
   * The mix of "challenge mich" and "erklär mir" is the mode switch: the coach
   * both quizzes and teaches, and the chips are where that becomes visible.
   */
  startPrompts: string[];
  available: boolean;
}[] = [
  {
    key: "aktivplan",
    label: "neue leben Aktivplan",
    description:
      "Die private Rentenversicherung im Zielsortiment: klassisch, hybrid oder fondsgebunden, mit laufendem Beitrag.",
    imageUrl: "/produkte/aktivplan.jpg",
    startPrompts: [
      "Challenge mich zu den Garantien.",
      "Challenge mich zum Zielmarkt.",
      "Erklär mir die Pflegekomponente.",
      "Erklär mir Zuzahlungen und Entnahmen.",
      "Fang mit einem Kundeneinwand an.",
    ],
    available: true,
  },
  {
    key: "berufsunfaehigkeit",
    label: "Einkommensschutz",
    description:
      "Berufsunfähigkeit und Grundfähigkeit der HDI: EGO Top, EGO Young und EGO Grundfähigkeitsschutz.",
    imageUrl: "/produkte/berufsunfaehigkeit.jpg",
    startPrompts: [
      "Challenge mich zu EGO Top und Grundfähigkeit.",
      "Challenge mich zur Risikoprüfung.",
      "Erklär mir KarrierePlus und den UpgradeJoker.",
      "Erklär mir die Leistung bei Krankschreibung.",
      "Fang mit einem Kundeneinwand an.",
    ],
    available: true,
  },
  {
    key: "foerderrente",
    label: "neue leben Förderrente",
    description:
      "Die staatlich geförderte Altersvorsorge ab 2027 mit 100 % Beitragserhaltungsgarantie, Nachfolgerin der Riester-Rente.",
    imageUrl: "/produkte/foerderrente.jpg",
    startPrompts: [
      "Challenge mich zu den Zulagen.",
      "Erklär mir die Günstigerprüfung.",
      "Challenge mich zum Tarif.",
      "Wie berate ich Riester-Bestandskunden?",
      "Fang mit einem Kundeneinwand an.",
    ],
    available: true,
  },
  {
    key: "basisrente",
    label: "Basisrente",
    description:
      "Die Vorsorge für einkommensstarke Kunden, deren Beiträge steuerlich absetzbar sind.",
    imageUrl: "/produkte/basisrente.jpg",
    // Empty on purpose: no snippets, no chips. Fill this when the product is
    // unlocked, otherwise the chips point at material that does not exist.
    startPrompts: [],
    available: false,
  },
];

export function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

/**
 * The first word of a name. A guess, not a fact: names from the login only ever
 * come as one string, and the advisor can correct the suggestion in onboarding.
 */
export function firstNameOf(name: string): string {
  return name.trim().split(/\s+/)[0] ?? "";
}
